import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import { useMerchantInvoiceDashboardOffline } from "../../../../offline";
import classes from "./charts.module.css";
import { MONTH_SHORT_NAMES, formatCompactNumber } from "./chartPeriodUtils";
import { generateUniqueColors } from "../../../../utils/chartColors";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const fallbackYear = new Date().getFullYear();

const VendorInvoiceDashboard = () => {
  const Data = useSelector((state) => state.loginData.value);
  const auth_token = Data?.auth_token;
  const user_token = Data?.user?.token || Data?.user_token;
  const org_id = Data?.org_id || Data?.user?.org_id || Data?.organization_id;
  const { data: dashboardSummary } = useMerchantInvoiceDashboardOffline(
    auth_token,
    user_token,
    org_id
  );

  const currentYear =
    dashboardSummary?.monthly_invoice_summary?.year || fallbackYear;
  const invoiceData = dashboardSummary?.monthly_invoice_summary?.data || [];

  const merchants = Array.isArray(invoiceData) ? invoiceData : [];
  const palette = generateUniqueColors(Math.max(merchants.length, 1));

  const totalYtd = useMemo(
    () =>
      merchants.reduce((sum, merchant) => {
        const totalValue =
          merchant?.[`total_ytd_${currentYear}`] ||
          merchant?.total ||
          merchant?.total_ytd;
        return sum + Number(totalValue || 0);
      }, 0),
    [currentYear, merchants]
  );

  const chartData = {
    labels: MONTH_SHORT_NAMES.map((month) => `${month} ${currentYear}`),
    datasets: merchants.map((merchant, index) => ({
      label: merchant?.merchant_name || `Merchant ${index + 1}`,
      data: MONTH_SHORT_NAMES.map((month) => {
        const key = `${month.toLowerCase()}_${currentYear}`;
        return Number(merchant?.[key] || 0);
      }),
      borderColor: palette[index],
      backgroundColor: palette[index],
      pointBackgroundColor: "#ffffff",
      pointBorderColor: palette[index],
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      borderWidth: 2.5,
      tension: 0.3,
      fill: false,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "nearest",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(9, 20, 42, 0.94)",
        borderColor: "rgba(120, 168, 255, 0.24)",
        borderWidth: 1,
        padding: 14,
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${formatCompactNumber(context.parsed?.y || 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#47607f",
          font: {
            family: "gilroyMedium",
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawBorder: false,
        },
        ticks: {
          color: "#47607f",
          precision: 0,
          font: {
            family: "gilroyMedium",
            size: 11,
          },
          callback: (value) => formatCompactNumber(value),
        },
      },
    },
  };

  return (
    <section className={classes.invoiceChartWrapper}>
      <div className={classes.chartHeader}>
        <div>
          <h2 className={classes.chartTitle}>Payment Processed Flow</h2>
          <p className={classes.chartSubtitle}>
            Merchant-wise monthly invoice movement for {currentYear}
          </p>
        </div>
      </div>

      <div className={classes.chartInsightStrip}>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Merchants Tracked</span>
          <strong className={classes.chartInsightValue}>{merchants.length}</strong>
        </div>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>YTD Volume</span>
          <strong className={classes.chartInsightValue}>
            {formatCompactNumber(totalYtd)}
          </strong>
        </div>
      </div>

      {chartData.datasets.length > 0 && (
        <div className={classes.chartLegend}>
          {chartData.datasets.map((dataset) => (
            <span
              key={dataset.label}
              className={`${classes.chartLegendItem} ${classes.chartLegendChip}`}
            >
              <span
                className={`${classes.chartLegendDot} ${classes.chartLegendLine}`}
                style={{ color: dataset.borderColor, background: "#ffffff" }}
              />
              {dataset.label}
            </span>
          ))}
        </div>
      )}

      <div className={classes.chartContainerLarge}>
        {merchants.length ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className={classes.chartEmptyState}>
            No monthly invoice summary available.
          </div>
        )}
      </div>
    </section>
  );
};

export default VendorInvoiceDashboard;
